import { ThemedLayout, useNotificationProvider } from '@refinedev/antd';
import { Authenticated, Refine } from '@refinedev/core';
import routerProvider from '@refinedev/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider, Spin } from 'antd';
import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Outlet, Route, Routes } from 'react-router';
import {
  ErrorBoundary,
  Header,
  LoginRedirect,
  PasswordChangeGuard,
  readSiderCollapsed,
  saveSiderCollapsed,
  Sider,
  Title,
} from '@/components';
import { getTheme, resources } from '@/config';
import { appQueryClient, refineOptions } from '@/config/refineOptions';
import { ROUTES } from '@/constants';
import { ThemeModeProvider, useThemeMode } from '@/context/themeMode';
import { useIsTouchTier } from '@/hooks/useIsTouchTier';
import { SetupToursProvider } from '@/features/setup-tours';
import { accessControlProvider } from '@/providers/accessControlProvider';
import { authProvider } from '@/providers/authProvider';
import { dataProvider } from '@/services';
import { protectedRoutes } from './protectedRoutes';

// Lazy load the public pages; the app-layout pages are in protectedRoutes.tsx
const Login = lazy(() => import('@/pages/auth/Login').then((m) => ({ default: m.Login })));
const Register = lazy(() => import('@/pages/auth/Register').then((m) => ({ default: m.Register })));
const ForgotPassword = lazy(() =>
  import('@/pages/auth/ForgotPassword').then((m) => ({ default: m.ForgotPassword })),
);
const ChangePasswordRequired = lazy(() =>
  import('@/pages/auth/ChangePasswordRequired').then((m) => ({
    default: m.ChangePasswordRequired,
  })),
);
const ResetPassword = lazy(() =>
  import('@/pages/auth/ResetPassword').then((m) => ({ default: m.ResetPassword })),
);

const ThemeConfigProvider = ({ children }: { children: ReactNode }): React.JSX.Element => {
  const { resolvedMode } = useThemeMode();
  const isTouchTier = useIsTouchTier();
  const selectedTheme = getTheme({ isDark: resolvedMode === 'dark', isTouchTier });
  return <ConfigProvider theme={selectedTheme}>{children}</ConfigProvider>;
};

export const App = (): React.JSX.Element => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={appQueryClient}>
        <ThemeModeProvider>
          <ThemeConfigProvider>
            <BrowserRouter>
              <SetupToursProvider>
                <Refine
                  dataProvider={dataProvider}
                  authProvider={authProvider}
                  accessControlProvider={accessControlProvider}
                  routerProvider={routerProvider}
                  notificationProvider={useNotificationProvider}
                  resources={resources}
                  options={refineOptions}
                >
                  <Routes>
                    {/* Public routes */}
                    <Route
                      path={ROUTES.LOGIN}
                      element={
                        <Suspense
                          fallback={
                            <div style={{ textAlign: 'center', padding: '50px' }}>
                              <Spin size="large" />
                            </div>
                          }
                        >
                          <Login />
                        </Suspense>
                      }
                    />
                    <Route
                      path={ROUTES.REGISTER}
                      element={
                        <Suspense
                          fallback={
                            <div style={{ textAlign: 'center', padding: '50px' }}>
                              <Spin size="large" />
                            </div>
                          }
                        >
                          <Register />
                        </Suspense>
                      }
                    />
                    <Route
                      path={ROUTES.FORGOT_PASSWORD}
                      element={
                        <Suspense
                          fallback={
                            <div style={{ textAlign: 'center', padding: '50px' }}>
                              <Spin size="large" />
                            </div>
                          }
                        >
                          <ForgotPassword />
                        </Suspense>
                      }
                    />
                    <Route
                      path={ROUTES.RESET_PASSWORD}
                      element={
                        <Suspense
                          fallback={
                            <div style={{ textAlign: 'center', padding: '50px' }}>
                              <Spin size="large" />
                            </div>
                          }
                        >
                          <ResetPassword />
                        </Suspense>
                      }
                    />

                    {/* A password someone else set: signed in, but outside the app layout */}
                    <Route
                      path={ROUTES.CHANGE_PASSWORD}
                      element={
                        <Authenticated key="change-password" fallback={<LoginRedirect />}>
                          <Suspense
                            fallback={
                              <div style={{ textAlign: 'center', padding: '50px' }}>
                                <Spin size="large" />
                              </div>
                            }
                          >
                            <ChangePasswordRequired />
                          </Suspense>
                        </Authenticated>
                      }
                    />

                    {/* Protected routes */}
                    <Route
                      element={
                        <Authenticated key="authenticated-layout" fallback={<LoginRedirect />}>
                          <PasswordChangeGuard>
                            <ThemedLayout
                              Header={() => <Header />}
                              Sider={Sider}
                              initialSiderCollapsed={readSiderCollapsed()}
                              onSiderCollapsed={saveSiderCollapsed}
                              Title={({ collapsed }) => <Title collapsed={collapsed} />}
                            >
                              <Suspense
                                fallback={
                                  <div style={{ textAlign: 'center', padding: '50px' }}>
                                    <Spin size="large" />
                                  </div>
                                }
                              >
                                <Outlet />
                              </Suspense>
                            </ThemedLayout>
                          </PasswordChangeGuard>
                        </Authenticated>
                      }
                    >
                      {protectedRoutes}
                    </Route>
                  </Routes>
                </Refine>
              </SetupToursProvider>
            </BrowserRouter>
          </ThemeConfigProvider>
        </ThemeModeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};
